import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Message as FeedIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getDashboardData } from '../../services/dashboardService';

// Import widgets
import RecentAnnouncements from './widgets/RecentAnnouncements';
import UpcomingEvents from './widgets/UpcomingEvents';
import PhotoGalleryPreview from './widgets/PhotoGalleryPreview';
import RecommendationsWidget from './widgets/RecommendationsWidget';
import RecipesWidget from './widgets/RecipesWidget';
import QuickStats from './widgets/QuickStats';
import QuickLinks from './widgets/QuickLinks';
import BirthdaysWidget from './widgets/BirthdaysWidget';
import DiscussionsWidget from './widgets/DiscussionsWidget';
import HealthUpdatesWidget from './widgets/HealthUpdatesWidget';
import GoalsWidget from './widgets/GoalsWidget';
import FunSocialWidget from './widgets/FunSocialWidget';
import DashboardSection from './widgets/DashboardSection';

// Import the social feed
import FamilyBoardFeed from './FamilyBoardFeed';

// Import dialogs
import RecipeDetailDialog from '../Recipes/RecipeDetailDialog';
import logger from '../../utils/logger';

/**
 * Family Board Dashboard
 * Comprehensive dashboard showing:
 * - Social Feed
 * - Recent Announcements
 * - Upcoming Events
 * - Photo Gallery Preview
 * - Recommendations (location-based)
 * - Recipes
 * - Quick Stats
 * - Quick Links to all features
 */
const FamilyBoardDashboard = () => {
  const { currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial tab from URL params
  const getInitialTab = () => {
    const tabParam = searchParams.get('tab');
    return tabParam === 'feed' ? 1 : 0;
  };

  // State
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [dashboardData, setDashboardData] = useState({
    announcements: [],
    events: [],
    photos: [],
    recommendations: [],
    recipes: [],
    discussions: [],
    healthUpdates: [],
    goals: [],
    funItems: [],
    birthdays: [],
    stats: {
      totalMembers: 0,
      totalAnnouncements: 0,
      totalEvents: 0,
      totalPhotos: 0,
      totalPosts: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Recipe detail dialog state
  const [recipeDetailOpen, setRecipeDetailOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);

  // Sync tab with URL changes (for browser back/forward)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const newTab = tabParam === 'feed' ? 1 : 0;
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [searchParams, activeTab]);

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!currentUser?.familyCode) {
        setError('No family code found. Please complete your profile.');
        return;
      }

      const data = await getDashboardData(currentUser.familyCode);
      setDashboardData(data);
    } catch (err) {
      logger.error('Error loading dashboard:', err);
      setError('Failed to load dashboard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Update URL to reflect tab state
    if (newValue === 1) {
      setSearchParams({ tab: 'feed' });
    } else {
      setSearchParams({});
    }
  };

  const handleRecipeClick = (recipe) => {
    setSelectedRecipe(recipe);
    setRecipeDetailOpen(true);
  };

  const handleCloseRecipeDetail = () => {
    setRecipeDetailOpen(false);
    setSelectedRecipe(null);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
          Family Board
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Your family's central hub for staying connected and informed
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs for Dashboard vs Feed */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab icon={<DashboardIcon />} label="Dashboard" />
          <Tab icon={<FeedIcon />} label="Social Feed" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box>
          {/* Quick Stats */}
          <Box sx={{ mb: 3 }}>
            <QuickStats stats={dashboardData.stats} loading={false} />
          </Box>

          {/* Main Content - Row-Based Sections */}

          {/* Section 1: What's New - Communication & Updates */}
          <DashboardSection title="What's New" columns={3}>
            <RecentAnnouncements
              announcements={dashboardData.announcements}
              loading={false}
            />
            <DiscussionsWidget
              discussions={dashboardData.discussions}
              loading={false}
            />
            <PhotoGalleryPreview
              photos={dashboardData.photos}
              loading={false}
            />
          </DashboardSection>

          {/* Section 2: Coming Up - Calendar & Date-based */}
          <DashboardSection title="Coming Up" columns={2}>
            <UpcomingEvents
              events={dashboardData.events}
              loading={false}
            />
            <BirthdaysWidget
              birthdays={dashboardData.birthdays}
              loading={false}
            />
          </DashboardSection>

          {/* Section 3: Family Wellness */}
          <DashboardSection title="Family Wellness" columns={2}>
            <HealthUpdatesWidget
              healthUpdates={dashboardData.healthUpdates}
              loading={false}
            />
            <GoalsWidget
              goals={dashboardData.goals}
              loading={false}
            />
          </DashboardSection>

          {/* Section 4: Discover & Fun */}
          <DashboardSection title="Discover & Fun" columns={3}>
            <RecommendationsWidget
              recommendations={dashboardData.recommendations}
              loading={false}
            />
            <RecipesWidget
              recipes={dashboardData.recipes}
              loading={false}
              onRecipeClick={handleRecipeClick}
              onRecipeAdded={loadDashboardData}
            />
            <FunSocialWidget
              funItems={dashboardData.funItems}
              loading={false}
            />
          </DashboardSection>

          {/* Quick Links */}
          <Box sx={{ mt: 3 }}>
            <QuickLinks />
          </Box>
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          <FamilyBoardFeed />
        </Box>
      )}

      {/* Recipe Detail Dialog */}
      <RecipeDetailDialog
        open={recipeDetailOpen}
        onClose={handleCloseRecipeDetail}
        recipe={selectedRecipe}
      />
    </Container>
  );
};

export default FamilyBoardDashboard;
